/**
 * tm/pages/FieldNotesPage.tsx — 「현장 노트」 (2026-11-01 지도사)
 *  · 배운 점: 상담 결과에 적은 「내가 배운 점」이 모이는 곳 + 따로 적기 — 언제든 열람 · 검색(업체 · 팀장님 이름 · 글)
 *  · 서비스 아이디어: 현장에서 팀장님 · 대표님께 배운 서비스 아이디어를 적어 두고 「검토 → 만드는 중 → 반영함」으로 챙긴다
 *    (예: 팀장님이 그 업체에 해 준 일을 한 장으로 들고 나가길 원함 · 보험 계약 전에 1년 업무 요약 제안서)
 *  화면: 왼쪽 목록(찾기 · 상태 칩 · 바로 적기) | 오른쪽 고치기(자동 저장) — 업무관리 다른 탭과 같은 크기 · 색
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { C, T, R, Btn, Badge, Empty, SearchInput, IMEArea, IMEText } from '../components/ui';
import * as N from '../api-notes';
import type { FieldNote, NoteCounts, NoteKind, NoteStatus, NoteTarget } from '../api-notes';

interface Props { onToast?: (msg: string, kind?: 'success' | 'error') => void }

const STATUS_TONE: Record<NoteStatus, { bg: string; fg: string }> = {
  memo: { bg: C.bg, fg: C.sub }, review: { bg: C.orangeLt, fg: C.orange }, dev: { bg: C.blueLt, fg: C.blue },
  done: { bg: C.greenLt, fg: C.green }, hold: { bg: C.bgAlt, fg: C.muted },
};
const STATUSES: NoteStatus[] = ['memo', 'review', 'dev', 'done', 'hold'];
const TARGETS = Object.keys(N.TARGET_LABEL) as Exclude<NoteTarget, ''>[];
const md = (s?: string | null): string => { const d = String(s || '').slice(0, 10); return d.length === 10 ? `${Number(d.slice(5, 7))}.${Number(d.slice(8, 10))}` : ''; };
const ymd = (s?: string | null): string => String(s || '').slice(0, 10).replace(/-/g, '.');

const box: React.CSSProperties = { background: C.white, border: `1px solid ${C.border}`, borderRadius: R.md };
const lbl: React.CSSProperties = { fontSize: T.xs, fontWeight: 700, color: C.sub, margin: '16px 0 6px' };
const inp: React.CSSProperties = { width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: T.md, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' };
const area = (h: number): React.CSSProperties => ({ ...inp, minHeight: h, lineHeight: 1.7, resize: 'vertical' });
const chip = (on: boolean, tone?: { bg: string; fg: string }): React.CSSProperties => ({
  height: 30, padding: '0 12px', borderRadius: R.sm, fontSize: T.xs, fontWeight: on ? 700 : 500, cursor: 'pointer', fontFamily: 'inherit',
  border: `1px solid ${on ? (tone ? tone.fg : C.accent) : C.border}`, background: on ? (tone ? tone.bg : C.accentLt) : C.white, color: on ? (tone ? tone.fg : C.accent) : C.sub,
});

export default function FieldNotesPage({ onToast }: Props) {
  const toast = useCallback((m: string, k?: 'success' | 'error') => { if (onToast) onToast(m, k); }, [onToast]);
  const [kind, setKind] = useState<NoteKind>('learn');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<NoteStatus | ''>('');
  const [list, setList] = useState<FieldNote[] | null>(null);
  const [counts, setCounts] = useState<NoteCounts | null>(null);
  const [tabN, setTabN] = useState<Record<NoteKind, number | null>>({ learn: null, idea: null });
  const [sel, setSel] = useState<string>('');
  const [quick, setQuick] = useState('');
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);

  const load = useCallback(async (keepSel?: string) => {
    const my = ++seq.current;
    try {
      const r = await N.listNotes({ kind, q: q.trim(), status: kind === 'idea' ? status : '' });
      if (my !== seq.current) return;
      setList(r.list); setCounts(r.counts);
      setTabN((t) => ({ ...t, [kind]: r.counts.total }));
      setSel((s) => (keepSel !== undefined ? keepSel : r.list.some((x) => x.id === s) ? s : (r.list[0] ? r.list[0].id : '')));
    } catch (e) { if (my === seq.current) { setList([]); toast((e as Error).message, 'error'); } }
  }, [kind, q, status, toast]);
  useEffect(() => { const t = window.setTimeout(() => { void load(); }, q ? 250 : 0); return () => window.clearTimeout(t); }, [load, q]);
  // 다른 탭 숫자도 처음 한 번
  useEffect(() => {
    const other: NoteKind = kind === 'learn' ? 'idea' : 'learn';
    if (tabN[other] === null) N.listNotes({ kind: other }).then((r) => setTabN((t) => ({ ...t, [other]: r.counts.total }))).catch(() => undefined);
  }, [kind, tabN]);

  // 상태 칩 숫자 — 적기 · 상태 바꾸기 뒤에 다시(목록은 그대로 두고 숫자만)
  const refreshCounts = useCallback(() => {
    N.listNotes({ kind }).then((r) => { setCounts(r.counts); setTabN((t) => ({ ...t, [kind]: r.counts.total })); }).catch(() => undefined);
  }, [kind]);
  const cur = useMemo(() => (list || []).find((x) => x.id === sel) || null, [list, sel]);

  const addQuick = async () => {
    const text = quick.trim();
    if (!text || busy) { if (!text) toast('적을 내용을 넣어 주세요'); return; }
    setBusy(true);
    try {
      const row = await N.createNote(kind === 'idea' ? { kind, scene: text } : { kind, body: text });
      setQuick(''); setStatus(''); setQ('');
      setList((l) => [row, ...(l || [])]); setSel(row.id);
      setTabN((t) => ({ ...t, [kind]: (t[kind] || 0) + 1 }));
      refreshCounts();
      toast(kind === 'idea' ? '아이디어를 적어 뒀어요 — 오른쪽에서 반영할 곳 · 상태를 골라 주세요' : '배운 점을 적어 뒀어요');
    } catch (e) { toast((e as Error).message, 'error'); } finally { setBusy(false); }
  };

  const patchLocal = (id: string, p: Partial<FieldNote>) => setList((l) => (l || []).map((x) => (x.id === id ? { ...x, ...p } : x)));
  const removed = (id: string) => {
    const next = (list || []).filter((x) => x.id !== id);
    setList(next); setSel(next[0] ? next[0].id : '');
    setTabN((t) => ({ ...t, [kind]: Math.max(0, (t[kind] || 1) - 1) }));
  };

  const tabs: { k: NoteKind; t: string }[] = [{ k: 'learn', t: '배운 점' }, { k: 'idea', t: '서비스 아이디어' }];
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: C.bg, minHeight: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 20px', background: C.white, borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
        <span style={{ fontSize: T.xl, fontWeight: 800, color: C.text }}>💡 현장 노트</span>
        <div role="tablist" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 150px)', gap: 6 }}>
          {tabs.map((x) => (
            <button key={x.k} role="tab" aria-selected={kind === x.k} type="button" style={{ ...chip(kind === x.k), height: 34, fontSize: T.sm }}
              onClick={() => { if (kind !== x.k) { setKind(x.k); setList(null); setSel(''); setStatus(''); setQ(''); setQuick(''); } }}>
              {x.t}{tabN[x.k] !== null ? ` ${tabN[x.k]}` : ''}
            </button>
          ))}
        </div>
        <span style={{ fontSize: T.xs, color: C.muted }}>
          {kind === 'learn' ? '상담 결과에 적은 「내가 배운 점」이 여기에 모여요 · 업체 · 팀장님 이름 · 글로 찾기' : '현장에서 들은 「이런 게 있으면 좋겠다」를 적어 두고 서비스에 반영해요'}
        </span>
      </div>
      <div style={{ flex: 1, display: 'flex', gap: 12, padding: 12, minHeight: 0 }}>
        {/* 왼쪽 — 찾기 · 상태 · 바로 적기 · 목록 */}
        <div style={{ ...box, width: 380, flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div style={{ padding: 12, borderBottom: `1px solid ${C.border}` }}>
            <IMEArea value={quick} onChange={setQuick} rows={2} style={{ ...area(56), fontSize: T.sm }}
              placeholder={kind === 'idea' ? '현장에서 들은 아이디어 — 예: 팀장님이 업체에 해 드린 일을 한 장으로 들고 나가고 싶어 하심' : '배운 점 — 예: 제조 대표님은 대출 금리보다 한도 · 속도를 먼저 물으심'}
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); void addQuick(); } }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
              <span style={{ flex: 1, fontSize: T.xs, color: C.muted }}>Ctrl + Enter 로도 적어져요</span>
              <Btn onClick={() => void addQuick()} disabled={busy} style={{ fontSize: T.xs, padding: '7px 14px' }}>{busy ? '적는 중…' : '＋ 적기'}</Btn>
            </div>
            <div style={{ marginTop: 10 }}><SearchInput value={q} onChange={setQ} placeholder={kind === 'idea' ? '아이디어 · 현장 · 알려 준 분 찾기' : '배운 점 · 업체 · 팀장님 찾기'} /></div>
            {kind === 'idea' && counts && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginTop: 10 }}>
                <button type="button" style={chip(status === '')} onClick={() => setStatus('')}>전체 {counts.total}</button>
                {STATUSES.map((s) => (
                  <button key={s} type="button" style={chip(status === s, STATUS_TONE[s])} onClick={() => setStatus(status === s ? '' : s)}>
                    {N.STATUS_LABEL[s]} {counts.status[s] || 0}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
            {list === null ? <div style={{ padding: 20, fontSize: T.sm, color: C.muted }}>불러오는 중…</div>
              : !list.length ? <Empty icon={kind === 'idea' ? '💡' : '📝'} message={q || status ? '찾는 노트가 없어요' : kind === 'idea' ? '아직 적은 아이디어가 없어요 — 위 칸에 적어 보세요' : '아직 배운 점이 없어요 — 상담 결과 화면의 「내가 배운 점」이나 위 칸에 적어 보세요'} />
              : list.map((n) => {
                const on = n.id === sel;
                const sub = kind === 'learn'
                  ? [md(n.consult_date || n.created_at), n.company_name, n.fp_name ? `${n.fp_name} 팀장님` : ''].filter(Boolean).join(' · ')
                  : [md(n.created_at), n.source_person].filter(Boolean).join(' · ');
                return (
                  <button key={n.id} type="button" onClick={() => setSel(n.id)}
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '12px 14px', border: 'none', borderBottom: `1px solid ${C.border}`, background: on ? C.accentLt : C.white, cursor: 'pointer', fontFamily: 'inherit' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {n.pinned && <span title="고정" style={{ fontSize: T.xs }}>📌</span>}
                      <span style={{ flex: 1, minWidth: 0, fontSize: T.md, fontWeight: 700, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{N.noteTitle(n)}</span>
                      {kind === 'idea' && <Badge bg={STATUS_TONE[n.status].bg} color={STATUS_TONE[n.status].fg}>{N.STATUS_LABEL[n.status]}</Badge>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                      <span style={{ flex: 1, minWidth: 0, fontSize: T.xs, color: C.sub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub || ' '}</span>
                      {kind === 'idea' && n.target && <span style={{ fontSize: T.xs, color: C.muted }}>{N.TARGET_LABEL[n.target as Exclude<NoteTarget, ''>]}</span>}
                    </div>
                  </button>
                );
              })}
          </div>
        </div>
        {/* 오른쪽 — 고치기 */}
        <div style={{ ...box, flex: 1, minWidth: 0, overflowY: 'auto' }}>
          {cur ? <NoteEditor key={cur.id} n={cur} onLocal={(p) => patchLocal(cur.id, p)} onRemoved={() => { removed(cur.id); refreshCounts(); }} onCounts={refreshCounts} toast={toast} />
            : <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted, fontSize: T.md }}>← 노트를 고르거나 새로 적어 주세요</div>}
        </div>
      </div>
    </div>
  );
}

/** 오른쪽 고치기 — 칸을 떠나거나 0.8초 쉬면 자동 저장 */
function NoteEditor({ n, onLocal, onRemoved, onCounts, toast }: { n: FieldNote; onLocal: (p: Partial<FieldNote>) => void; onRemoved: () => void; onCounts: () => void; toast: (m: string, k?: 'success' | 'error') => void }) {
  const [f, setF] = useState<FieldNote>(n);
  const [saved, setSaved] = useState<'' | 'saving' | 'ok' | 'err'>('');
  const [arm, setArm] = useState(false);
  const pending = useRef<Partial<FieldNote>>({});
  const timer = useRef<number | undefined>(undefined);
  const flush = useCallback(async () => {
    window.clearTimeout(timer.current);
    const p = pending.current; pending.current = {};
    if (!Object.keys(p).length) return;
    setSaved('saving');
    try { await N.updateNote(n.id, p); setSaved('ok'); if ('status' in p || 'target' in p) onCounts(); } catch (e) { setSaved('err'); pending.current = { ...p, ...pending.current }; toast((e as Error).message, 'error'); }
  }, [n.id, toast, onCounts]);
  useEffect(() => () => { void flush(); }, [flush]);   // 다른 노트로 옮기면 남은 것 저장
  const set = (p: Partial<FieldNote>, now?: boolean) => {
    setF((x) => ({ ...x, ...p })); onLocal(p);
    pending.current = { ...pending.current, ...p };
    window.clearTimeout(timer.current);
    if (now) void flush(); else timer.current = window.setTimeout(() => { void flush(); }, 800);
  };
  const remove = async () => {
    if (!arm) { setArm(true); window.setTimeout(() => setArm(false), 4000); return; }
    try { pending.current = {}; await N.deleteNote(n.id); toast('노트를 지웠어요'); onRemoved(); } catch (e) { toast((e as Error).message, 'error'); }
  };
  const idea = n.kind === 'idea';
  return (
    <div style={{ padding: '18px 24px 28px', maxWidth: 860 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: T.xs, color: C.muted, flex: 1 }}>
          {ymd(n.created_at)} 적음{n.created_by_name ? ` · ${n.created_by_name}` : ''}
          {saved === 'saving' ? ' · 저장 중…' : saved === 'ok' ? ' · 저장됨' : saved === 'err' ? ' · 저장 못 함' : ''}
        </span>
        <button type="button" style={chip(f.pinned)} onClick={() => set({ pinned: !f.pinned }, true)}>📌 {f.pinned ? '고정됨' : '위에 고정'}</button>
        <button type="button" onClick={() => void remove()} style={{ ...chip(arm, { bg: C.redLt, fg: C.red }), minWidth: 92 }}>{arm ? '정말 지우기' : '지우기'}</button>
      </div>
      {!idea && n.consultation_id && (
        <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 8, background: C.bgAlt, border: `1px solid ${C.border}`, fontSize: T.sm, color: C.sub }}>
          🤝 {ymd(n.consult_date)} 상담{n.company_name ? ` · ${n.company_name}` : ''}{n.fp_name ? ` · ${n.fp_name} 팀장님` : ''} <span style={{ color: C.muted }}>— 상담 결과 화면에서 고쳐도 여기 같이 바뀌어요</span>
        </div>
      )}
      <div style={lbl}>제목 <span style={{ fontWeight: 400, color: C.muted }}>(비우면 첫 줄)</span></div>
      <IMEText value={f.title} onChange={(v) => set({ title: v })} onBlur={() => void flush()} style={inp} placeholder={idea ? '예: 해 드린 일 한 장 브리핑' : '예: 제조 대표님은 한도 · 속도부터'} maxLength={120} />
      {idea && (
        <>
          <div style={lbl}>현장 상황 — 누가 · 어떤 자리에서 · 무엇을 원했나</div>
          <IMEArea value={f.scene} onChange={(v) => set({ scene: v })} onBlur={() => void flush()} style={area(90)} placeholder={'예: 최은연 팀장님 — 기존 고객 연말 미팅 때 「지금까지 해 드린 일과 그게 돈으로 얼마였는지」 한 장으로 들고 가고 싶다고 하심'} />
        </>
      )}
      <div style={lbl}>{idea ? '어떻게 반영할까 — 우리 서비스에 넣을 모습' : '배운 점'}</div>
      <IMEArea value={f.body} onChange={(v) => set({ body: v })} onBlur={() => void flush()} style={area(idea ? 110 : 220)} placeholder={idea ? '예: fphub 컨설팅 로드맵 화면에서 「중간 브리핑」으로 한 장 출력 — 해 드린 일 · 돌아간 돈 vs 비용' : '상담에서 새로 알게 된 것 · 다음에 써먹을 말 · 조심할 점'} />
      {idea && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <div style={lbl}>알려 준 분</div>
              <IMEText value={f.source_person} onChange={(v) => set({ source_person: v })} onBlur={() => void flush()} style={inp} placeholder="예: 최은연 팀장님" maxLength={60} />
            </div>
            <div>
              <div style={lbl}>반영할 곳</div>
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${TARGETS.length}, 1fr)`, gap: 6 }}>
                {TARGETS.map((t) => <button key={t} type="button" style={chip(f.target === t)} onClick={() => set({ target: f.target === t ? '' : t }, true)}>{N.TARGET_LABEL[t]}</button>)}
              </div>
            </div>
          </div>
          <div style={lbl}>상태</div>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${STATUSES.length}, 1fr)`, gap: 6, maxWidth: 560 }}>
            {STATUSES.map((s) => <button key={s} type="button" style={chip(f.status === s, STATUS_TONE[s])} onClick={() => set({ status: s }, true)}>{N.STATUS_LABEL[s]}</button>)}
          </div>
          {(f.status === 'dev' || f.status === 'done' || f.done_note) && (
            <>
              <div style={lbl}>반영 메모 — 어디에 어떻게 넣었나</div>
              <IMEArea value={f.done_note} onChange={(v) => set({ done_note: v })} onBlur={() => void flush()} style={area(70)} placeholder="예: fphub 리포트 › 컨설팅 로드맵 · 11월 배포" />
            </>
          )}
        </>
      )}
    </div>
  );
}
